import * as Sentry from "@sentry/browser";

import { GENERIC_ERROR } from "client/constants";
import { queryClient } from "client/context/query-client";
import * as AuthService from "client/services/auth";

import { generateRandomId } from "./generate-random-id";
import { BASE_ROUTE } from "./route-utils";
import { getSessionId } from "./session";

const sessionId = getSessionId();

export async function apiClient<T>(
  endpoint: string,
  {
    data = null,
    headers: customHeaders = {},
    method,
    externalApiRequest = false,
    config: customConfig = undefined,
  }: {
    data?: any;
    headers?: Headers | any;
    method?: string;
    externalApiRequest?: boolean;
    config?: RequestInit | undefined;
  } = {}
): Promise<T> {
  const correlationId = generateRandomId();
  const headers = externalApiRequest
    ? customHeaders
    : {
        "Content-Type": data ? "application/json" : undefined,
        "X-Correlation-Id": correlationId,
        "X-Session-Id": sessionId,
        "X-App-Version": "redesign",
        ...customHeaders,
      };

  const config = Boolean(customConfig)
    ? customConfig
    : {
        method: method ?? (data ? "POST" : "GET"),
        body: data ? JSON.stringify(data) : undefined,
        headers,
      };
  Sentry.configureScope((scope: any) => {
    scope.setTag("correlation_id", correlationId);
  });
  return window.fetch(endpoint, config).then(async (response) => {
    if (response.status === 401 && !externalApiRequest) {
      // clear all query cache
      queryClient.clear();
      // clear cookie session
      await AuthService.logout();
      // refresh the page and land on signup page
      window.location.assign(`${window.location.origin}${BASE_ROUTE}`); // TODO: remove app on rollout
      return Promise.reject({ message: "Please re-authenticate." });
    }

    try {
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      const respText = await response.text();
      const errorMsg = `${GENERIC_ERROR} (${respText})`;
      return Promise.reject(new Error(errorMsg));
    } catch (ex) {
      return Promise.reject(ex);
    }
  });
}
